import React, { useState, useEffect } from 'react';
import { Form, Button, Image, Spinner, Alert, Row, Col, Card } from 'react-bootstrap';
import { FaUpload, FaTimes, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { uploadAPI } from '../services/api';

const MultipleImageUpload = ({ currentImages = [], onImagesChange, label = 'Product Images', maxImages = 10 }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [images, setImages] = useState(currentImages || []);

  useEffect(() => {
    setImages(currentImages || []);
  }, [currentImages]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Check if adding these files would exceed max
    if (images.length + files.length > maxImages) {
      setError(`Maximum ${maxImages} images allowed`);
      return;
    }

    // Validate files
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setError('Please select only image files');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Each image should be less than 5MB');
        return;
      }
    }

    setError('');
    setUploading(true);

    try {
      const uploadedUrls = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('image', file);

        const response = await uploadAPI.uploadImage(formData);
        const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
        const imageUrl = `${baseUrl}${response.data.imageUrl}`;
        uploadedUrls.push(imageUrl);
      }

      const newImages = [...images, ...uploadedUrls];
      setImages(newImages);
      onImagesChange(newImages);
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.response?.data?.message || 'Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    onImagesChange(newImages);
  };

  const handleMoveLeft = (index) => {
    if (index === 0) return;
    const newImages = [...images];
    [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
    setImages(newImages);
    onImagesChange(newImages);
  };

  const handleMoveRight = (index) => {
    if (index === images.length - 1) return;
    const newImages = [...images];
    [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
    setImages(newImages);
    onImagesChange(newImages);
  };

  return (
    <div>
      <Form.Label>{label}</Form.Label>
      
      {error && <Alert variant="danger" className="mb-2" dismissible onClose={() => setError('')}>{error}</Alert>}
      
      {/* Uploaded Images Grid */}
      {images.length > 0 && (
        <Row className="mb-3">
          {images.map((imageUrl, index) => (
            <Col xs={6} md={4} lg={3} key={index} className="mb-3">
              <Card>
                <Card.Img 
                  variant="top" 
                  src={imageUrl} 
                  style={{ height: '150px', objectFit: 'cover' }}
                />
                <Card.Body className="p-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">#{index + 1}</small>
                    <div>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-1 p-1"
                        onClick={() => handleMoveLeft(index)}
                        disabled={index === 0}
                        title="Move Left"
                      >
                        <FaArrowLeft size={12} />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-1 p-1"
                        onClick={() => handleMoveRight(index)}
                        disabled={index === images.length - 1}
                        title="Move Right"
                      >
                        <FaArrowRight size={12} />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        className="p-1"
                        onClick={() => handleRemoveImage(index)}
                        title="Remove"
                      >
                        <FaTimes size={12} />
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Upload Button */}
      {images.length < maxImages && (
        <div className="border rounded p-4 text-center bg-light">
          <FaUpload size={40} className="text-muted mb-2" />
          <p className="text-muted mb-2">
            {images.length > 0 ? 'Add more images' : 'Upload product images'}
          </p>
          <p className="text-muted small mb-2">
            {images.length} / {maxImages} images uploaded
          </p>
          <Form.Control
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            disabled={uploading}
            multiple
          />
        </div>
      )}

      {uploading && (
        <div className="mt-2 text-center">
          <Spinner animation="border" size="sm" className="me-2" />
          <small>Uploading images...</small>
        </div>
      )}

      <Form.Text className="text-muted d-block mt-2">
        Supported formats: JPEG, PNG, GIF, WebP (Max 5MB each). First image will be the main product image.
      </Form.Text>
    </div>
  );
};

export default MultipleImageUpload;
